import { EmpenhoSiafAlocacaoService } from '../../services/EmpenhoSiafAlocacaoService';
import { SnackBarModule } from '../../modules/SnackBarModule';

export default {
  name: 'AlocacaoElementosPrimarios',
  props: {
    empenhoId: {
      type: [Number, String],
      required: true
    }
  },
  data: () => ({
    headers: [
      { text: 'Elemento Primário', value: 'descricao', sortable: true },
      { text: 'Valor', value: 'valor', sortable: true, align: 'end' },
      { text: 'Tipo', value: 'tipo', sortable: true },
      { text: 'Ações', value: 'acoes', sortable: false, align: 'center' }
    ],
    alocacoes: [],
    elementosElegiveis: [],
    empenho: null,
    resumo: {
      valor_empenho: 0,
      valor_alocado: 0,
      saldo_disponivel: 0,
      percentual_alocado: 0
    },
    novaAlocacao: {
      elemento_primario: null,
      valor: ''
    },
    valorFormatado: '',
    loadingAlocacoes: false,
    loadingElementos: false,
    loadingAlocar: false,
    loadingExcluir: false,
    formValid: false,
    dialogExcluir: false,
    alocacaoParaExcluir: null
  }),
  computed: {
    rules() {
      return {
        required: v => !!v || 'Campo obrigatório',
        valorMinimo: () => {
          const numerico = parseFloat(this.novaAlocacao.valor);
          if (isNaN(numerico) || numerico <= 0) {
            return 'Informe um valor maior que zero';
          }
          return true;
        }
      };
    }
  },
  mounted() {
    this.carregarDados();
  },
  methods: {
    async carregarDados() {
      await this.carregarEmpenho();
      
      await Promise.all([
        this.carregarAlocacoes(),
        this.carregarElementosElegiveis(),
        this.carregarResumo()
      ]);
    },

    async carregarEmpenho() {
      try {
        const EmpenhoSIAFService = require('../../services/EmpenhoSIAFService').EmpenhoSIAFService;
        const response = await EmpenhoSIAFService.show(this.empenhoId);
        
        if (!response.error && response.data) {
          this.empenho = response.data;
        }
      } catch (error) {
        // Silent error
      }
    },

    async carregarAlocacoes() {
      try {
        this.loadingAlocacoes = true;
        const response = await EmpenhoSiafAlocacaoService.listarAlocacoes(this.empenhoId);
        
        if (!response.error && response.data) {
          this.alocacoes = response.data;
        } else {
          SnackBarModule(response.message || 'Erro ao carregar alocações', 'error');
        }
      } catch (error) {
        SnackBarModule('Erro ao carregar alocações', 'error');
      } finally {
        this.loadingAlocacoes = false;
      }
    },

    async carregarElementosElegiveis() {
      try {
        this.loadingElementos = true;
        
        const anoEmpenho = this.empenho?.ano_empenho || this.empenho?.exercicio_ano || new Date().getFullYear();
        
        const response = await EmpenhoSiafAlocacaoService.listarElementosElegiveis({ ano: anoEmpenho });
        
        if (!response.error && response.data) {
          this.elementosElegiveis = response.data;
          
          if (this.elementosElegiveis.length === 0) {
            SnackBarModule(`Não há elementos primários disponíveis para o ano ${anoEmpenho}`, 'warning');
          }
        } else {
          SnackBarModule(response.message || 'Erro ao carregar elementos primários', 'error');
        }
      } catch (error) {
        SnackBarModule('Erro ao carregar elementos primários', 'error');
      } finally {
        this.loadingElementos = false;
      }
    },

    async carregarResumo() {
      try {
        const response = await EmpenhoSiafAlocacaoService.obterResumo(this.empenhoId);
        
        if (!response.error && response.data) {
          this.resumo = {
            valor_empenho: parseFloat(response.data.valor_empenho) || 0,
            valor_alocado: parseFloat(response.data.valor_alocado) || 0,
            saldo_disponivel: parseFloat(response.data.saldo_disponivel) || 0,
            percentual_alocado: parseFloat(response.data.percentual_alocado) || 0
          };
        }
      } catch (error) {
        // Silent error
      }
    },

    async alocar() {
      if (!this.$refs.formAlocacao.validate()) {
        return;
      }

      try {
        this.loadingAlocar = true;

        const valorNumerico = parseFloat(this.novaAlocacao.valor);
        
        if (isNaN(valorNumerico) || valorNumerico <= 0) {
          SnackBarModule('Informe um valor válido maior que zero', 'error');
          this.loadingAlocar = false;
          return;
        }

        const dados = {
          elemento_primario_id: this.novaAlocacao.elemento_primario.id,
          elemento_primario_type: this.novaAlocacao.elemento_primario.type,
          valor: valorNumerico.toFixed(2)
        };

        const response = await EmpenhoSiafAlocacaoService.criarAlocacao(this.empenhoId, dados);

        if (!response.error) {
          SnackBarModule('Alocação criada com sucesso', 'success');
          this.limparFormulario();
          await this.carregarDados();
        } else {
          SnackBarModule(response.message || 'Erro ao criar alocação', 'error');
        }
      } catch (error) {
        SnackBarModule('Erro ao criar alocação', 'error');
      } finally {
        this.loadingAlocar = false;
      }
    },

    confirmarExclusao(alocacao) {
      this.alocacaoParaExcluir = alocacao;
      this.dialogExcluir = true;
    },

    async excluirAlocacao() {
      if (!this.alocacaoParaExcluir) {
        return;
      }

      try {
        this.loadingExcluir = true;

        const response = await EmpenhoSiafAlocacaoService.excluirAlocacao(this.alocacaoParaExcluir.id);

        if (!response.error) {
          SnackBarModule('Alocação excluída com sucesso', 'success');
          this.dialogExcluir = false;
          this.alocacaoParaExcluir = null;
          await this.carregarDados();
        } else {
          SnackBarModule(response.message || 'Erro ao excluir alocação', 'error');
        }
      } catch (error) {
        SnackBarModule('Erro ao excluir alocação', 'error');
      } finally {
        this.loadingExcluir = false;
      }
    },

    limparFormulario() {
      this.novaAlocacao = {
        elemento_primario: null,
        valor: ''
      };
      this.valorFormatado = '';
      this.$refs.formAlocacao.resetValidation();
    },

    formatCurrency(value) {
      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) {
        return 'R$ 0,00';
      }
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(numericValue);
    },

    getTipoColor(tipo) {
      const colors = {
        'EMENDA ESTADUAL': 'blue',
        'EMENDA FEDERAL': 'purple',
        'CONVÊNIO FEDERAL': 'purple',
        'CONVÊNIO': 'purple',
        'REFORMA ESTADUAL': 'blue',
        'GENÉRICO': 'grey'
      };
      return colors[tipo] || 'grey';
    },

    formatarValorMonetario(valor) {
      if (!valor) {
        this.valorFormatado = '';
        this.novaAlocacao.valor = 0;
        return;
      }
      
      const apenasNumeros = String(valor).replace(/\D/g, '');
      
      if (apenasNumeros === '') {
        this.valorFormatado = '';
        this.novaAlocacao.valor = 0;
        return;
      }
      
      const valorNumerico = parseFloat(apenasNumeros) / 100;
      
      this.valorFormatado = valorNumerico.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      this.novaAlocacao.valor = valorNumerico;
    },

    validarValorMonetario(valor) {
      const valorNumerico = parseFloat(valor);
      return !isNaN(valorNumerico) && valorNumerico > 0;
    }
  }
};

